import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Gift, Copy, Check, Share2, Users, Coins, Rocket, Clock, Ticket } from "lucide-react";
import api from "../lib/api";
import { NeonButton, NeonCard, Pill } from "./Neon";
import { gbp, timeParts } from "../lib/format";

// Refer-a-Friend card (gold accent). Reads GET /me/referrals. Hidden entirely when the
// programme is disabled AND no launch promo is active — matches the mobile app.
export const ReferralCard = () => {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let active = true;
    api.get("/me/referrals")
      .then(({ data }) => { if (active) setData(data); })
      .catch(() => {})
      .finally(() => { if (active) setLoaded(true); });
    return () => { active = false; };
  }, []);

  if (!loaded || !data) return null;
  const launch = data.launch || {};
  const visible = data.enabled || launch.active;
  if (!visible) return null;

  const shareLink = data.share_link || (typeof window !== "undefined" && data.referral_code
    ? `${window.location.origin}/?ref=${data.referral_code}` : "");

  const copy = async () => {
    try {
      await navigator.clipboard?.writeText(data.referral_code || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      toast.success("Referral code copied.");
    } catch { toast.error("Could not copy — copy it manually."); }
  };

  const share = async () => {
    const text = `Join me on Richi£Rich Competitions and we both win! Use my code ${data.referral_code}.`;
    try {
      if (navigator.share) await navigator.share({ title: "Richi£Rich Competitions", text, url: shareLink });
      else { await navigator.clipboard?.writeText(`${text} ${shareLink}`); toast.success("Share message copied to clipboard."); }
    } catch { /* user cancelled */ }
  };

  const lt = launch.ends_at ? timeParts(launch.ends_at) : null;

  return (
    <NeonCard variant="flow" className="mt-5" innerClassName="p-6 border border-[rgba(255,210,74,.35)]" data-testid="referral-card">
      <div className="flex items-center gap-2">
        <Gift className="h-5 w-5 text-[var(--neon-gold)]" />
        <p className="font-display uppercase text-xl text-white">Refer a Friend</p>
      </div>

      {launch.active && (
        <div className="mt-3 flex items-start gap-3 rounded-xl border border-[rgba(255,210,74,.5)] bg-[rgba(255,210,74,.08)] px-4 py-3" data-testid="referral-launch-banner">
          <Rocket className="h-5 w-5 shrink-0 text-[var(--neon-gold)]" />
          <div className="text-sm">
            <p className="font-display uppercase tracking-wide text-[var(--neon-gold)]">Launch bonus — {launch.free_tickets} FREE tickets each</p>
            <p className="mt-0.5 text-xs text-[var(--text-muted)]">
              You AND your friend each get {launch.free_tickets} free ticket{launch.free_tickets === 1 ? "" : "s"}
              {launch.competition?.title ? <> to <span className="text-white">{launch.competition.title}</span></> : null} once they join &amp; verify.
              {lt && !lt.done && <span className="ml-1 inline-flex items-center gap-1"><Clock className="h-3 w-3" /> Ends in {lt.d}d {lt.h}h</span>}
            </p>
          </div>
        </div>
      )}

      {!launch.active && data.enabled && (
        <p className="mt-2 text-sm text-[var(--text-muted)]">
          Share your code — once your friend deposits &amp; wagers {gbp(data.min_spend || 0)} you get{" "}
          <span className="text-[var(--neon-gold)]">{gbp(data.reward || 0)}</span> site credit, and they get{" "}
          <span className="text-[var(--neon-gold)]">{gbp(data.friend_discount_amount ?? data.reward ?? 0)}</span> off their first {gbp(data.friend_discount_min ?? data.min_spend ?? 0)}+ basket.{" "}
          You&apos;ll <span className="text-[var(--neon-gold)]">both get a Golden Ticket</span> — a free entry into any competition with tickets up to £1.
        </p>
      )}

      {(Array.isArray(data.golden_tickets) || data.golden_tickets_available != null) && (
        <div className="mt-3 flex items-center justify-between rounded-xl border border-[rgba(255,210,74,.4)] bg-[rgba(255,210,74,.06)] px-4 py-2.5" data-testid="golden-tickets">
          <span className="flex items-center gap-1.5 text-sm text-[var(--neon-gold)]"><Ticket className="h-4 w-4" /> Golden Tickets</span>
          <span className="font-display text-lg text-white">{data.golden_tickets_available ?? (data.golden_tickets || []).filter((g) => (g.status || "available") === "available").length} available</span>
        </div>
      )}

      {/* Code + actions */}
      <div className="mt-4 flex flex-col sm:flex-row items-stretch gap-2">
        <div className="flex-1 flex items-center justify-between rounded-xl border border-white/10 bg-[var(--surface-2)] px-4 py-3">
          <span className="font-mono text-lg tracking-wider text-white" data-testid="referral-code">{data.referral_code || "—"}</span>
          <button onClick={copy} data-testid="referral-copy" aria-label="Copy code" className="text-[var(--neon-gold)] hover:brightness-110">
            {copied ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
          </button>
        </div>
        <NeonButton variant="pink" onClick={share} data-testid="referral-share"><Share2 className="h-4 w-4" /> Share</NeonButton>
      </div>

      {/* Stats */}
      <div className="mt-4 grid grid-cols-2 gap-3">
        <div className="rounded-xl border border-white/10 px-4 py-3">
          <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-[var(--text-muted)]"><Users className="h-3.5 w-3.5" /> Friends joined</p>
          <p className="mt-1 font-display text-2xl text-white" data-testid="referral-count">{data.referral_count || 0}</p>
        </div>
        <div className="rounded-xl border border-white/10 px-4 py-3">
          <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-[var(--text-muted)]"><Coins className="h-3.5 w-3.5" /> Credit earned</p>
          <p className="mt-1 font-display text-2xl text-[var(--neon-gold)]" data-testid="referral-earnings">{gbp(data.referral_earnings || 0)}</p>
        </div>
      </div>
    </NeonCard>
  );
};
